import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Loader2, 
  ArrowLeft, 
  Plus, 
  ChevronUp,
  ChevronDown, 
  Trash2, 
  MessageSquare,
  CheckCircle,
  ArrowRight,
  X
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface JobQuestion {
  id: string;
  question_text: string;
  question_type: "aberta" | "multipla_escolha" | "curta";
  options: string[] | null;
  order_index: number;
  is_required: boolean;
}

const QUESTION_TYPES = [
  { value: "aberta", label: "Resposta aberta", description: "Texto longo" },
  { value: "multipla_escolha", label: "Múltipla escolha", description: "Opções definidas" },
  { value: "curta", label: "Resposta curta", description: "Texto curto" },
];

const EmpresaVagaPerguntasPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { loading: accessLoading, companyId } = useCompanyAccess(true);
  
  const [jobTitle, setJobTitle] = useState("");
  const [questions, setQuestions] = useState<JobQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New question form state
  const [newQuestionText, setNewQuestionText] = useState("");
  const [newQuestionType, setNewQuestionType] = useState<"aberta" | "multipla_escolha" | "curta">("aberta");
  const [newOptions, setNewOptions] = useState<string[]>(["", ""]);
  const [newIsRequired, setNewIsRequired] = useState(false);

  // Fetch job info and existing questions
  useEffect(() => {
    const fetchData = async () => {
      if (!jobId || !companyId) return;
      
      try {
        // Fetch job title and verify ownership
        const { data: job, error: jobError } = await supabase
          .from("jobs")
          .select("title, company_id")
          .eq("id", jobId)
          .maybeSingle();
        
        if (jobError) throw jobError;
        if (!job) {
          toast({ title: "Vaga não encontrada", variant: "destructive" });
          navigate("/empresa/dashboard");
          return;
        }
        
        // Verify company ownership
        if (job.company_id !== companyId) {
          toast({ title: "Acesso negado", description: "Você não tem permissão para acessar esta vaga", variant: "destructive" });
          navigate("/empresa/dashboard");
          return;
        }
        
        setJobTitle(job.title);
        
        // Fetch existing questions
        const { data: questionsData, error: questionsError } = await supabase
          .from("job_questions")
          .select("*")
          .eq("job_id", jobId)
          .order("order_index", { ascending: true });
        
        if (questionsError) throw questionsError;
        setQuestions((questionsData || []).map(q => ({
          ...q,
          question_type: q.question_type as "aberta" | "multipla_escolha" | "curta"
        })));
      } catch (error: any) {
        console.error("Error fetching data:", error);
        toast({ title: "Erro ao carregar dados", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [jobId, companyId, navigate]);

  const handleAddQuestion = async () => {
    if (!newQuestionText.trim()) {
      toast({ title: "Digite a pergunta", variant: "destructive" });
      return;
    }
    
    if (newQuestionType === "multipla_escolha") {
      const validOptions = newOptions.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        toast({ title: "Adicione pelo menos 2 opções", variant: "destructive" });
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const newQuestion = {
        job_id: jobId,
        question_text: newQuestionText.trim(),
        question_type: newQuestionType,
        options: newQuestionType === "multipla_escolha" 
          ? newOptions.filter(opt => opt.trim()) 
          : null,
        order_index: questions.length,
        is_required: newIsRequired,
      };
      
      const { data, error } = await supabase
        .from("job_questions")
        .insert(newQuestion)
        .select()
        .single();
      
      if (error) throw error;
      
      setQuestions([...questions, {
        ...data,
        question_type: data.question_type as "aberta" | "multipla_escolha" | "curta"
      }]);
      
      // Reset form
      setNewQuestionText("");
      setNewQuestionType("aberta");
      setNewOptions(["", ""]);
      setNewIsRequired(false);
      
      toast({ title: "Pergunta adicionada!" });
    } catch (error: any) {
      console.error("Error adding question:", error);
      toast({ title: "Erro ao adicionar pergunta", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const { error } = await supabase
        .from("job_questions")
        .delete()
        .eq("id", questionId);
      
      if (error) throw error;
      
      setQuestions(questions.filter(q => q.id !== questionId));
      toast({ title: "Pergunta excluída" });
    } catch (error: any) {
      console.error("Error deleting question:", error);
      toast({ title: "Erro ao excluir pergunta", variant: "destructive" });
    }
  };

  const handleMoveQuestion = async (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    
    // Update order_index for both questions
    newQuestions[index].order_index = index;
    newQuestions[newIndex].order_index = newIndex;
    
    setQuestions(newQuestions);
    
    // Update in database
    try {
      await Promise.all([
        supabase.from("job_questions").update({ order_index: index }).eq("id", newQuestions[index].id),
        supabase.from("job_questions").update({ order_index: newIndex }).eq("id", newQuestions[newIndex].id),
      ]);
    } catch (error) {
      console.error("Error reordering questions:", error);
    }
  };

  const handleAddOption = () => {
    setNewOptions([...newOptions, ""]);
  };

  const handleRemoveOption = (index: number) => {
    if (newOptions.length <= 2) return;
    setNewOptions(newOptions.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index: number, value: string) => {
    const updated = [...newOptions];
    updated[index] = value;
    setNewOptions(updated);
  };

  const handleFinish = () => {
    toast({ 
      title: "Vaga publicada!", 
      description: `${questions.length} pergunta(s) adicionada(s).` 
    });
    navigate("/empresa/dashboard");
  };

  const handleSkip = () => {
    toast({ title: "Vaga publicada sem perguntas personalizadas." });
    navigate("/empresa/dashboard");
  };

  if (accessLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <EmpresaSidebar />
        <main className="flex-1 p-4 md:p-6 bg-muted/30 overflow-auto">
          <div className="mobile-container max-w-3xl md:mx-auto space-y-6 pt-14 md:pt-0">
            {/* Header */}
            <div className="space-y-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/empresa/dashboard")}
                className="gap-1 px-0 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <div>
                <p className="text-sm text-muted-foreground">Etapa 2 de 2</p>
                <h1 className="text-2xl md:text-3xl font-display font-bold">{jobTitle}</h1>
              </div>
            </div>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  Perguntas Personalizadas
                </CardTitle>
                <CardDescription>
                  Adicione perguntas que os candidatos deverão responder ao se candidatar a esta vaga.
                  Esta etapa é opcional.
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Questions List */}
            {questions.length > 0 && (
              <div className="space-y-3">
                {questions.map((question, index) => (
                  <Card key={question.id} className="relative">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveQuestion(index, "up")}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveQuestion(index, "down")}
                            disabled={index === questions.length - 1}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium">
                              {index + 1}. {question.question_text}
                            </p>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir pergunta?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteQuestion(question.id)}>
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {QUESTION_TYPES.find(t => t.value === question.question_type)?.label}
                            </Badge>
                            {question.is_required && (
                              <Badge variant="outline" className="text-destructive border-destructive">
                                Obrigatória
                              </Badge>
                            )}
                          </div>
                          
                          {question.question_type === "multipla_escolha" && question.options && (
                            <div className="pl-4 space-y-1 text-sm text-muted-foreground">
                              {question.options.map((opt, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <span className="w-4 h-4 rounded-full border border-muted-foreground/30" />
                                  {opt}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Add New Question Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Adicionar Nova Pergunta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Pergunta</Label>
                  <Textarea
                    placeholder="Digite a pergunta que deseja fazer aos candidatos..."
                    value={newQuestionText}
                    onChange={(e) => setNewQuestionText(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Resposta</Label>
                    <Select 
                      value={newQuestionType} 
                      onValueChange={(v) => setNewQuestionType(v as "aberta" | "multipla_escolha" | "curta")}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUESTION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label} ({type.description})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center pt-2 md:pt-8">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="required"
                        checked={newIsRequired}
                        onCheckedChange={(checked) => setNewIsRequired(checked as boolean)}
                      />
                      <Label htmlFor="required" className="font-normal cursor-pointer">
                        Pergunta obrigatória
                      </Label>
                    </div>
                  </div>
                </div>

                {newQuestionType === "multipla_escolha" && (
                  <div className="space-y-3">
                    <Label>Opções de Resposta</Label>
                    {newOptions.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full border border-muted-foreground/30 flex-shrink-0" />
                        <Input
                          placeholder={`Opção ${index + 1}`}
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                        />
                        {newOptions.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveOption(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddOption}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar opção
                    </Button>
                  </div>
                )}

                <Button 
                  onClick={handleAddQuestion} 
                  disabled={saving || !newQuestionText.trim()}
                  className="w-full"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Adicionar Pergunta
                </Button>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse md:flex-row justify-between items-stretch md:items-center gap-3 pt-4">
              <Button variant="outline" onClick={handleSkip} className="w-full md:w-auto">
                Pular etapa
              </Button>
              <Button onClick={handleFinish} className="w-full md:w-auto">
                <CheckCircle className="h-4 w-4 mr-2" />
                Finalizar e Publicar
                {questions.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {questions.length}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default EmpresaVagaPerguntasPage;
